import React from 'react'

import { EuiBadge, EuiBasicTable, EuiBasicTableColumn, EuiButton, EuiCallOut, EuiLink, EuiPanel, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import history from 'services/HistoryService'

import { QuickCampaignChannel, QuickCampaignResult, QuickCampaignResultStatus, channelLabels } from './quickCampaignModels'

export interface QuickCampaignResultsPanelProps {
  results: QuickCampaignResult[]
  isSubmittedForApproval: boolean
}

const statusLabels: Record<QuickCampaignResultStatus, string> = {
  [QuickCampaignResultStatus.Created]: 'Created',
  [QuickCampaignResultStatus.CreatedWithWarnings]: 'Created with warnings',
  [QuickCampaignResultStatus.Failed]: 'Failed'
}

const statusColors: Record<QuickCampaignResultStatus, string> = {
  [QuickCampaignResultStatus.Created]: 'success',
  [QuickCampaignResultStatus.CreatedWithWarnings]: 'warning',
  [QuickCampaignResultStatus.Failed]: 'danger'
}

const columns: EuiBasicTableColumn<QuickCampaignResult>[] = [
  { field: 'campaignName', name: 'Campaign' },
  { field: 'channel', name: 'Channel', render: (channel: QuickCampaignChannel) => channelLabels[channel] },
  {
    field: 'status',
    name: 'Status',
    render: (status: QuickCampaignResultStatus, result: QuickCampaignResult) => (
      <React.Fragment>
        <EuiBadge color={statusColors[status]}>{statusLabels[status]}</EuiBadge>
        {result.message && (
          <EuiText size='s' color={status === QuickCampaignResultStatus.Failed ? 'danger' : 'subdued'}>
            <p>{result.message}</p>
          </EuiText>
        )}
      </React.Fragment>
    )
  },
  {
    name: 'Campaign Link',
    render: (result: QuickCampaignResult) =>
      result.campaignId ? (
        <EuiLink href={history.createHref({ pathname: `/campaigns/edit/${result.campaignId}` })} target='_blank'>
          Open
        </EuiLink>
      ) : null
  }
]

const titleOf = (createdCount: number, requestedCount: number) => (createdCount === requestedCount ? 'Campaigns created' : createdCount === 0 ? 'Campaigns could not be created' : `${createdCount} of ${requestedCount} campaigns created`)

export const QuickCampaignResultsPanel: React.FC<QuickCampaignResultsPanelProps> = ({ results, isSubmittedForApproval }) => {
  const createdCount = results.filter(result => !!result.campaignId).length
  return (
    <EuiPanel grow={false} className='eui-xScroll'>
      <EuiTitle size='s'>
        <h2>{titleOf(createdCount, results.length)}</h2>
      </EuiTitle>
      <EuiSpacer size='m' />
      {isSubmittedForApproval && createdCount > 0 && (
        <React.Fragment>
          <EuiCallOut title='Your campaigns were submitted for approval' iconType='check' color='success' size='s' />
          <EuiSpacer size='m' />
        </React.Fragment>
      )}
      <EuiBasicTable items={results} columns={columns} itemId='channel' />
      <EuiSpacer size='m' />
      <EuiButton fill onClick={() => history.push('/campaigns')}>
        Go to campaigns
      </EuiButton>
    </EuiPanel>
  )
}
